/** The live-data pill in the header: a pulsing light (green = fresh, amber =
 *  older than the staleness limit, red = no data), the price count, when the
 *  oldest snapshot was collected, and a countdown to the next scheduled
 *  refresh that ticks every minute. Per-vendor detail on hover. */
import { useEffect, useState } from 'react'
import { api } from '../api'
import { VENDOR_LABEL } from '../settings'
const vendorName = (p: string) => ((VENDOR_LABEL as Record<string, string>)[p] ?? p).toUpperCase()
import { useAsync } from './useAsync'

function age(hours: number): string {
  if (hours < 1) return 'minutes ago'
  if (hours < 24) return `${Math.round(hours)}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function countdown(epoch: number, now: number): string {
  const s = Math.max(0, epoch - now)
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
  if (s < 60) return 'any moment'
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m} min`
}

/** `compact`: the light and one word, the rest on hover; otherwise it all reads inline. */
export function FreshnessBadge({ compact = false, onData }: { compact?: boolean; onData?: () => void } = {}) {
  // re-read every 5 minutes so a refresh that lands shows without a reload
  const [tick, setTick] = useState(0)
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 5 * 60_000); return () => clearInterval(t) }, [])
  const { data, error } = useAsync(() => api.freshness(), [tick])
  const [now, setNow] = useState(() => Date.now() / 1000)
  useEffect(() => { const t = setInterval(() => setNow(Date.now() / 1000), 30_000); return () => clearInterval(t) }, [])

  if (error) return <span className="live off"><span className="led" />no price data</span>
  if (!data) return <span className="live"><span className="led wait" />…</span>

  const oldest = data.providers.reduce((a, b) => (a.age_hours > b.age_hours ? a : b), data.providers[0])
  if (!oldest) return <span className="live off"><span className="led" />no snapshots yet</span>
  const total = data.providers.reduce((n, p) => n + p.records, 0)
  const pinned = data.providers.filter((p) => p.pinned).map((p) => vendorName(p.provider))
  const next = data.next_refresh_at ? countdown(data.next_refresh_at, now) : null
  const state = data.stale ? 'Stale' : 'Live'
  const summary = `${total.toLocaleString()} prices, ${data.stale ? 'oldest' : 'refreshed'} ${age(oldest.age_hours)}`
    + (data.auto_refresh === false ? ', automatic refresh off' : next ? `, next refresh in ${next}` : '')

  if (!compact) {
    return (
      <span className={data.stale ? 'live stale' : 'live'} title={summary}>
        <span className="led" aria-hidden="true" />
        <b>{state}</b>
        <span className="sep">·</span>{total.toLocaleString()} prices
        <span className="sep">·</span>{data.stale ? `oldest ${age(oldest.age_hours)}` : `refreshed ${age(oldest.age_hours)}`}
        {data.auto_refresh === false ? <><span className="sep">·</span><span className="next">auto-refresh <b>off</b></span></>
          : next ? <><span className="sep">·</span><span className="next">next in <b>{next}</b></span></> : null}
        {pinned.length ? <><span className="sep">·</span>{pinned.join(', ')} on earlier prices</> : null}
      </span>
    )
  }
  // One word and the light; everything else on hover or keyboard focus.
  return (
    <span className={data.stale ? 'live stale' : 'live'} tabIndex={0} aria-label={`${state}: ${summary}`}>
      <span className="led" aria-hidden="true" />
      <b>{state}</b>
      <span className="livemore" role="tooltip">
        <span className="row"><b>{total.toLocaleString()}</b> prices</span>
        <span className="row">{data.stale ? 'oldest' : 'refreshed'} <b>{age(oldest.age_hours)}</b></span>
        {data.auto_refresh === false ? <span className="row next">auto-refresh <b>off</b></span>
          : next ? <span className="row next">next in <b>{next}</b>{data.refresh_interval_hours ? ` · every ${data.refresh_interval_hours} h` : ''}</span> : null}
        {pinned.length ? <span className="row">{pinned.join(', ')} on earlier prices</span> : null}
        <span className="vendors">
          {data.providers.map((p) => (
            <span className="row muted" key={p.provider}>{vendorName(p.provider)} · {p.records.toLocaleString()} · {age(p.age_hours)}{p.pinned ? ' · restored' : ''}</span>
          ))}
        </span>
        {onData ? <button type="button" className="linklike datalink" onClick={onData}>Data and sources ›</button> : null}
      </span>
    </span>
  )
}
